/**
 * Carries a file or link a visitor picked on a transcription landing page
 * through sign-up to the app. The file waits in this browser's IndexedDB
 * (it can't ride along in a URL or survive the OAuth redirect in memory) and
 * is taken out once, by /audios, which then offers to transcribe it.
 *
 * Never throws: when storage is unavailable (private windows, quota), the
 * visitor simply picks the file again after signing in.
 */

const DB_NAME = 'ep-transcription-handoff';
const STORE = 'handoff';
const KEY = 'pending';
/** A hand-off older than this is dropped instead of offered. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type TranscriptionHandoff =
  | { kind: 'file'; file: File; savedAt: number }
  | { kind: 'link'; link: string; savedAt: number };

const openDb = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const run = async <T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
) => {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const request = action(transaction.objectStore(STORE));
      transaction.oncomplete = () => resolve(request.result);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    db.close();
  }
};

/** Saves the file or link; returns false when it couldn't be stored. */
export async function saveTranscriptionHandoff(
  value: { file: File } | { link: string },
): Promise<boolean> {
  const record: TranscriptionHandoff =
    'file' in value
      ? { kind: 'file', file: value.file, savedAt: Date.now() }
      : { kind: 'link', link: value.link, savedAt: Date.now() };
  try {
    await run('readwrite', (store) => store.put(record, KEY));
    return true;
  } catch {
    return false;
  }
}

/** Returns the saved hand-off (if fresh) and removes it, so it's offered once. */
export async function takeTranscriptionHandoff(): Promise<TranscriptionHandoff | null> {
  try {
    const record = await run<TranscriptionHandoff | undefined>(
      'readonly',
      (store) => store.get(KEY) as IDBRequest<TranscriptionHandoff | undefined>,
    );
    if (!record) return null;
    await run('readwrite', (store) => store.delete(KEY));
    if (Date.now() - record.savedAt > MAX_AGE_MS) return null;
    if (record.kind === 'file' && !(record.file instanceof Blob)) return null;
    if (record.kind === 'link' && typeof record.link !== 'string') return null;
    return record;
  } catch {
    return null;
  }
}
